import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2,
  Camera,
  LogOut,
  User as UserIcon,
  Bookmark,
  PlusCircle,
  CalendarClock,
  ChevronRight,
  Sun,
  Moon,
  Monitor,
  Trash2,
  Gift,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useSaved } from "../hooks/useSaved";
import { supabase } from "../lib/supabaseClient";
import { signOut, deleteAccount } from "../lib/authApi";
import { shareInvite } from "../lib/referral";
import {
  getOrCreateProfile,
  saveProfile,
  uploadProfileImage,
  getSignedImageUrl,
  removeProfileImage,
} from "../lib/profileApi";
import { getListingStatus, startListingCheckout, claimFoundingOffer } from "../lib/listingApi";

const fieldClass =
  "border-white/10 bg-[#121212] text-white placeholder:text-white/30 focus-visible:ring-[#ff2a00]";

export default function Profile() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { theme, setTheme } = useTheme();
  const { saved } = useSaved();
  const fileRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState("");
  const [organiserName, setOrganiserName] = useState("");
  const [organiserBio, setOrganiserBio] = useState("");
  const [hasOrganiser, setHasOrganiser] = useState(false);
  // Whether the organiser profile was ALREADY complete when the page loaded — so
  // the Founding Organiser offer is only claimed on a genuine transition to
  // complete (no backfill for already-qualifying accounts).
  const wasCompleteRef = useRef(false);
  const [hasEvents, setHasEvents] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);

  const [imagePath, setImagePath] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const [listing, setListing] = useState(null);
  const [startingCheckout, setStartingCheckout] = useState(false);

  const needsPaidListing =
    listing && !listing.is_admin && listing.free_remaining <= 0 && listing.paid_available <= 0;

  const handleAddEvent = async () => {
    if (!organiserName.trim() || !organiserBio.trim()) {
      toast.error("Complete your Organiser / Business Name and Short Bio before adding an event.");
      return;
    }
    if (!needsPaidListing) {
      navigate("/add-event");
      return;
    }
    setStartingCheckout(true);
    try {
      const res = await startListingCheckout();
      if (res?.native) {
        toast.success("Listing credit added");
        navigate("/add-event");
        return;
      }
      if (res?.checkout_url) {
        window.location.href = res.checkout_url;
        return;
      }
      throw new Error("Could not start checkout.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Could not start checkout.");
      setStartingCheckout(false);
    }
  };

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    let active = true;
    (async () => {
      try {
        const p = await getOrCreateProfile(user.id);
        if (!active) return;
        setDisplayName(p.display_name || "");
        setOrganiserName(p.organiser_name || "");
        setOrganiserBio(p.organiser_bio || "");
        setHasOrganiser(Boolean(p.organiser_name));
        wasCompleteRef.current = Boolean(
          (p.organiser_name || "").trim() && (p.organiser_bio || "").trim()
        );
        setImagePath(p.profile_image_path || null);
        if (p.profile_image_path) {
          const url = await getSignedImageUrl(p.profile_image_path);
          if (active) setImageUrl(url);
        }
        // Also expose My Events access if the user owns any events, even without
        // organiser info saved (owner RLS allows counting their own rows).
        const { count } = await supabase
          .from("Events")
          .select("id", { count: "exact", head: true })
          .eq("owner_id", user.id);
        if (active) setHasEvents((count || 0) > 0);
        getListingStatus().then((s) => active && setListing(s)).catch(() => {});
      } catch (e) {
        toast.error("Could not load your profile.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [user]);

  const chooseImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !user) return;
    setUploading(true);
    const prevPath = imagePath; // exact path of the image being replaced
    try {
      const path = await uploadProfileImage(user.id, file);
      await saveProfile(user.id, { profile_image_path: path });
      setImagePath(path);
      setImageUrl(await getSignedImageUrl(path));
      toast.success("Profile image updated");
      // Remove the PREVIOUS file only after the new one is saved (never the
      // current image). Non-fatal, but a block is reported.
      if (prevPath && prevPath !== path) {
        const rmError = await removeProfileImage(prevPath);
        if (rmError) {
          toast.warning("Previous image could not be removed from storage: " + rmError);
        }
      }
    } catch (err) {
      toast.error(err.message || "Could not upload image.");
    } finally {
      setUploading(false);
    }
  };

  const saveBasic = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await saveProfile(user.id, {
        display_name: displayName,
        organiser_name: organiserName,
        organiser_bio: organiserBio,
        profile_image_path: imagePath,
      });
      setHasOrganiser(Boolean(organiserName.trim()));
      toast.success("Profile saved");
      await maybeClaimFounding();
    } catch (err) {
      toast.error("Could not save your profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // "List an Event" for a user without organiser info: capture name + bio, then go.
  const continueToAddEvent = async () => {
    if (!user) return;
    if (!organiserName.trim() || !organiserBio.trim()) {
      toast.error("Complete your Organiser / Business Name and Short Bio before adding an event.");
      return;
    }
    setContinuing(true);
    try {
      await saveProfile(user.id, {
        display_name: displayName,
        organiser_name: organiserName,
        organiser_bio: organiserBio,
        profile_image_path: imagePath,
      });
      setHasOrganiser(true);
      await maybeClaimFounding();
      navigate("/add-event");
    } catch (err) {
      toast.error("Could not save organiser info. Please try again.");
    } finally {
      setContinuing(false);
    }
  };

  // Fire the Founding Organiser claim exactly once, only when the profile has just
  // become complete (was incomplete at load). No-op for already-complete accounts.
  const maybeClaimFounding = async () => {
    const nowComplete = !!(organiserName.trim() && organiserBio.trim());
    if (!nowComplete || wasCompleteRef.current) return;
    wasCompleteRef.current = true;
    try {
      const res = await claimFoundingOffer();
      if (res?.claimed) {
        getListingStatus().then(setListing).catch(() => {});
        if (res.remaining >= 0) {
          toast.success(`Founding organiser offer unlocked — ${res.remaining} free listings added`);
        }
      }
    } catch {
      /* non-fatal: offer may be exhausted or unavailable */
    }
  };

  const logout = async () => {
    await signOut();
    toast.success("Logged out");
    navigate("/");
  };

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await deleteAccount();
      await signOut();
      try {
        localStorage.removeItem("roadrnr_saved");
      } catch {
        /* ignore */
      }
      setConfirmDelete(false);
      toast.success("Your account and data have been permanently deleted.");
      navigate("/");
    } catch (err) {
      toast.error(err?.message || "Could not delete your account. Please try again.");
      setDeleting(false);
    }
  };

  if (authLoading || (user && loading)) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-[#050505]">
        <Loader2 className="h-6 w-6 animate-spin text-[#ff2a00]" />
      </div>
    );
  }
  if (!user) return null;

  return (
    <div data-testid="profile-page" className="min-h-[100dvh] bg-[#050505] pb-28">
      <div className="sticky top-0 z-30 border-b border-white/10 bg-[#050505]/85 px-5 pb-4 pt-6 backdrop-blur-xl">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-white">
          Profile
        </h1>
        <p className="mt-1 truncate text-sm text-white/45">{user.email}</p>
      </div>

      <div className="mx-auto max-w-md px-5 pt-6">
        {/* Avatar with camera button (badge sits ABOVE and overlaps the edge) */}
        <div className="flex flex-col items-center">
          <div className="relative h-28 w-28">
            <button
              data-testid="profile-image-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex h-28 w-28 items-center justify-center overflow-hidden rounded-full border border-white/10 bg-[#121212]"
            >
              {imageUrl ? (
                <img src={imageUrl} alt="Profile" className="h-full w-full object-cover" />
              ) : (
                <UserIcon className="h-10 w-10 text-white/30" />
              )}
            </button>
            <button
              data-testid="profile-image-camera"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              aria-label="Change profile photo"
              className="absolute -bottom-1 -right-1 z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#050505] bg-[#ff2a00] shadow-lg shadow-black/40 transition-transform duration-200 active:scale-90"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin text-white" />
              ) : (
                <Camera className="h-[18px] w-[18px] text-white" />
              )}
            </button>
          </div>
          <p className="mt-3 text-xs text-white/40">Add a profile photo</p>
        </div>

        {/* Display name */}
        <div className="mt-8 space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-white/50">
            Display name
          </Label>
          <Input
            data-testid="profile-display-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="How you'll appear on RoadRnr"
            className={fieldClass}
          />
        </div>

        {/* Organiser / business name */}
        <div className="mt-4 space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-white/50">
            Organiser / business name
          </Label>
          <Input
            data-testid="organiser-name"
            value={organiserName}
            onChange={(e) => setOrganiserName(e.target.value)}
            placeholder="e.g. Ace Cafe London"
            className={fieldClass}
          />
        </div>

        {/* Short bio */}
        <div className="mt-4 space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-white/50">
            Short bio
          </Label>
          <Textarea
            data-testid="organiser-bio"
            value={organiserBio}
            onChange={(e) => setOrganiserBio(e.target.value)}
            rows={3}
            className={fieldClass}
          />
        </div>

        <button
          data-testid="profile-save"
          onClick={saveBasic}
          disabled={saving}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#ff2a00] py-3.5 text-base font-bold text-white transition-transform duration-200 active:scale-[0.98] disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save Profile"}
        </button>

        {/* Referral / invite — available to EVERY signed-in user */}
        <button
          data-testid="profile-referral"
          onClick={shareInvite}
          className="mt-8 flex w-full items-center gap-3 rounded-2xl border border-white/10 bg-[#0d0d0d] px-4 py-4 text-left transition-colors duration-200 hover:bg-[#151515]"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#ff2a00]/15">
            <Gift className="h-5 w-5 text-[#ff2a00]" />
          </span>
          <span className="flex-1">
            <span className="block text-[15px] font-semibold text-white">Get 3 free listings</span>
            <span className="block text-xs text-white/40">Invite a friend and you'll both get 3</span>
          </span>
          <span className="shrink-0 text-xs font-semibold text-[#ff2a00]">Share invite link →</span>
        </button>

        {/* Saved events */}
        <button
          data-testid="profile-saved-events"
          onClick={() => navigate("/saved")}
          className="mt-3 flex w-full items-center gap-3 rounded-2xl border border-white/10 bg-[#0d0d0d] px-4 py-4 text-left transition-colors duration-200 hover:bg-[#151515]"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#ff2a00]/15">
            <Bookmark className="h-5 w-5 text-[#ff2a00]" />
          </span>
          <span className="flex-1">
            <span className="block text-[15px] font-semibold text-white">Saved events</span>
            <span className="block text-xs text-white/40">
              {saved.length} {saved.length === 1 ? "event" : "events"} saved
            </span>
          </span>
          <ChevronRight className="h-4 w-4 text-white/30" />
        </button>

        {/* My Events — visible whenever the user has organiser info OR owns events */}
        {(hasOrganiser || hasEvents) && (
          <button
            data-testid="profile-my-events"
            onClick={() => navigate("/my-events")}
            className="mt-3 flex w-full items-center gap-3 rounded-2xl border border-white/10 bg-[#0d0d0d] px-4 py-4 text-left transition-colors duration-200 hover:bg-[#151515]"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#ff2a00]/15">
              <CalendarClock className="h-5 w-5 text-[#ff2a00]" />
            </span>
            <span className="flex-1">
              <span className="block text-[15px] font-semibold text-white">My events</span>
              <span className="block text-xs text-white/40">View, edit and boost your events</span>
            </span>
            <ChevronRight className="h-4 w-4 text-white/30" />
          </button>
        )}
        {hasOrganiser ? (
          <div data-testid="organiser-section" className="mt-8">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/40">
              Organiser
            </h2>
            <div className="mt-3 space-y-4">
              <button
                data-testid="profile-add-event"
                onClick={handleAddEvent}
                disabled={startingCheckout}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#ff2a00] py-3.5 text-base font-bold text-white transition-transform duration-200 active:scale-[0.98] disabled:opacity-60"
              >
                {startingCheckout ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <PlusCircle className="h-5 w-5" /> {needsPaidListing ? "Add another event · £1.99" : "Add Event"}
                  </>
                )}
              </button>
            </div>
          </div>
        ) : setupOpen ? (
          <div data-testid="organiser-setup" className="mt-8">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/40">
              Organiser setup
            </h2>
            <p className="mt-2 text-sm text-white/50">
              Just two quick details so people know who's running the event.
            </p>
            <div className="mt-4 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-white/50">
                  Organiser / business name
                </Label>
                <Input
                  data-testid="setup-organiser-name"
                  value={organiserName}
                  onChange={(e) => setOrganiserName(e.target.value)}
                  placeholder="e.g. Ace Cafe London"
                  className={fieldClass}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-white/50">
                  Short organiser bio
                </Label>
                <Textarea
                  data-testid="setup-organiser-bio"
                  value={organiserBio}
                  onChange={(e) => setOrganiserBio(e.target.value)}
                  placeholder="Tell people about the events you run..."
                  rows={3}
                  className={fieldClass}
                />
              </div>
              <button
                data-testid="setup-continue"
                onClick={continueToAddEvent}
                disabled={continuing}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#ff2a00] py-3.5 text-base font-bold text-white transition-transform duration-200 active:scale-[0.98] disabled:opacity-60"
              >
                {continuing ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  "Continue to Add Event"
                )}
              </button>
            </div>
          </div>
        ) : (
          <button
            data-testid="profile-list-event"
            onClick={() => setSetupOpen(true)}
            className="mt-8 flex w-full items-center justify-center gap-2 rounded-full border border-[#ff2a00]/40 bg-[#ff2a00]/10 py-3.5 text-base font-bold text-white transition-colors duration-200 hover:bg-[#ff2a00]/15"
          >
            <PlusCircle className="h-5 w-5 text-[#ff2a00]" /> List an Event
          </button>
        )}

        <div data-testid="appearance-section" className="mt-8">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-white/50">
            Appearance
          </h2>
          <div className="mt-3 grid grid-cols-3 gap-2 rounded-2xl border border-white/10 bg-[#121212] p-1.5">
            {[
              { key: "system", label: "System", Icon: Monitor },
              { key: "light", label: "Light", Icon: Sun },
              { key: "dark", label: "Dark", Icon: Moon },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                data-testid={`theme-${key}`}
                onClick={() => setTheme(key)}
                className={`flex flex-col items-center gap-1.5 rounded-xl py-3 text-xs font-semibold transition-colors ${
                  theme === key
                    ? "bg-[#ff2a00] text-white"
                    : "text-white/60 hover:bg-white/5"
                }`}
              >
                <Icon className="h-5 w-5" />
                {label}
              </button>
            ))}
          </div>
        </div>

        <button
          data-testid="profile-logout"
          onClick={logout}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-white/10 py-3.5 text-base font-semibold text-white/70 transition-colors hover:text-white"
        >
          <LogOut className="h-4 w-4" /> Log out
        </button>

        <button
          data-testid="profile-delete-account"
          onClick={() => setConfirmDelete(true)}
          className="mt-8 flex w-full items-center justify-center gap-2 rounded-full border border-[#ff2a00]/40 py-3.5 text-base font-semibold text-[#ff2a00] transition-colors hover:bg-[#ff2a00]/10"
        >
          <Trash2 className="h-4 w-4" /> Delete account
        </button>
        <p className="mt-2 text-center text-xs text-white/40">
          Learn more on our{" "}
          <button
            data-testid="profile-delete-info-link"
            onClick={() => navigate("/delete-account")}
            className="font-semibold text-white/60 underline"
          >
            account deletion page
          </button>
          .
        </p>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={(o) => !deleting && setConfirmDelete(o)}>
        <AlertDialogContent data-testid="delete-account-dialog" className="border-white/10 bg-[#0d0d0d] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#ff2a00]">Permanently delete your account?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              This cannot be undone. Your profile, saved events, your events and their
              images, boosts, and banner ads will be permanently deleted. Limited
              transaction records may be retained for legal and accounting purposes.
              You will be signed out immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="delete-account-cancel"
              disabled={deleting}
              className="border-white/15 bg-transparent text-white hover:bg-white/5"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-account-confirm"
              onClick={(e) => {
                e.preventDefault();
                handleDeleteAccount();
              }}
              disabled={deleting}
              className="bg-[#ff2a00] text-white hover:bg-[#ff2a00]/90"
            >
              {deleting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Delete account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <input
        ref={fileRef}
        data-testid="profile-image-input"
        type="file"
        accept="image/*"
        onChange={chooseImage}
        className="hidden"
      />
    </div>
  );
}
